/*
 *
 * Copyright 2019, White River Computing LLC. All rights reserved.
 * For licensing information, please contact Charlie Flock (cflock@whiterivercomputing.com or 888-802-6340)
 */
import './WrcImageMap.css';

import React from "react";
import ReactDOM from "react-dom";
import {Alert, Button, ButtonGroup, Card, Elevation} from "@blueprintjs/core";
import {IconNames} from "@blueprintjs/icons";
import {Intent} from "@blueprintjs/core/lib/esm/index";
import {WrcForm} from "./WrcForm.js"
import {WrcServer} from "./WrcServer.js";
import {WrcCardHeader} from "./WrcCardHeader";

export const boxColor = index => 'box color' + (index % 10);
export const configKey = props => `map_${props.references}_${props.fkey}_${props.table_name}_isExpanded`;

export class WrcImageMap extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            is_loaded: false,
            image: React.createRef()
        };
    }

    componentDidMount() {
        let data = this.props.data.slice();
        const key = configKey(this.props);
        const curExpandedList = this.props.config[key] || [];
        data.forEach((d, index) => {d.isExpanded = curExpandedList[index] || false});
        this.setState({
            data: data,
            is_loaded: true
        });
    }

    componentDidUpdate(prevProps, prevState) {
        let debugging = prevProps;
    }

    render() {
        let height= `${this.props.height}px`;
        if (this.state.is_loaded) {
            let areas = this.props.areas.map(function(area) {
                return (
                    <area
                        onClick={(e) => this.mapClick(e)}
                        shape={area.shape}
                        coords={area.coords}
                        title={area.description}
                        alt={area.description} />
                );
            }.bind(this));

            let image_location = this.state.image.current.getBoundingClientRect();
            const markerName = this.props.marker_name || 'marker';
            const markerNameCap = markerName[0].toUpperCase() + markerName.slice(1);
            const headerTitle = markerNameCap + ' Location';
            let markers = [];
            let markerCards = [];
            if (this.state.data !== undefined) {
                markers = this.state.data.map(function(marker, index) {
                    const color = boxColor(index);
                    return (
                        <div style={{position: 'absolute', top: parseInt(marker.map_y) - 12, left: parseInt(marker.map_x) - 12}}>
                            <span className={color} onClick={this.editItemClick(index)}>
                                {index + 1}
                            </span>
                        </div>
                    );
                }.bind(this));

                markerCards = this.state.data.map(function(marker, index) {
                    const color = boxColor(index);
                    let rowfields = [];
                    if (this.props.columns !== undefined) {
                        rowfields = this.props.columns.map(function(column, index) {
                            return (
                                <tr>
                                    <td className="wd-table-label">{column.header}:</td>
                                    <td className="wd-table-data">{marker[column.field]}</td>
                                </tr>
                            );
                        });
                    }
                    return (
                        <React.Fragment>
                            <tr>
                                <th><span className={color}>{index+1}</span></th>
                                <th><ButtonGroup minimal={true} large={false} style={{float: 'right'}}>
                                    <Button icon={IconNames.EDIT} onClick={this.editItemClick(index)}/>
                                    <Button icon={IconNames.TRASH} onClick={this.deleteItemClick(index, markerName)}/>
                                </ButtonGroup></th>
                            </tr>
                            <tr style={{display: 'none'}}><td></td><td></td></tr>
                            {rowfields}
                        </React.Fragment>
                    );
                }.bind(this));
            }

            return (
                <div className="wd-record">
                    <Card className={'wd-card-m-bottom'} interactive={false} elevation={Elevation.TWO}>
                        <div style={{display: 'flex'}}>
                            <div className="wd-imagemap-left" >
                                <WrcCardHeader title={headerTitle}/>
                                <p style={{width: this.props.width}}>Enter a new {markerName} by clicking on the diagram.</p>
                            </div>
                            <div className="wd-imagemap-right" style={{overflowY: 'auto'}}>
                                <WrcCardHeader title={markerNameCap + ' Details'}/>
                            </div>
                        </div>
                        <div style={{height: height, display: 'flex'}}>
                            <div className="wd-imagemap-left">
                                <div style={{position: 'relative'}}>
                                    <img ref={this.state.image} src={this.props.image_source} usemap={'#' + this.props.map_name} alt={this.props.image_description} style={{width: this.props.width}}/>
                                    <map name={this.props.map_name}>
                                        {areas}
                                    </map>
                                    {markers}
                                </div>
                            </div>
                            <div className="wd-imagemap-right" style={{overflowY: 'auto'}}>
                                <table className={'bp3-html-table bp3-small bp3-html-table-striped'} style={{width: '100%'}}>
                                    <tbody>
                                        {markerCards}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Card>
                </div>
            );
        }
        else {
            return (
                <div className="wd-record">
                    <Card className={'wd-card-m-bottom'} interactive={false} elevation={Elevation.TWO} style={{height:height}}>
                        <div ref={this.state.image}>Loading...</div>
                    </Card>
                </div>
            );
        }
    }

    getData(content) {
        let data = Object.assign({}, content[this.props.table_name]);
        const key = configKey(this.props);
        const curExpandedList = this.props.config[key] || [];
        data.forEach((d, index) => {d.isExpanded = curExpandedList[index] || false});
        this.setState({
            data: data,
            is_loaded: true
        });
    }

    mapClick(e) {
        let image_location = this.state.image.current.getBoundingClientRect();
        let map_x = Math.round(e.clientX - image_location.left);
        let map_y = Math.round(e.clientY - image_location.top);
        let default_fields = [
            {name: 'map_x', value: map_x},
            {name: 'map_y', value: map_y}];
        ReactDOM.render(
            <WrcForm form_id={this.props.form_id} default_fields={default_fields} fkey={this.props.fkey} refresh={this.props.refresh} />,
            document.querySelector('#form'));
    }

    /*
     * collapse/expand marker detail card
     */
    collapseItemClick = (index) => (e) => {
        const curMarkerList = this.state.data.slice();
        curMarkerList[index].isExpanded = curMarkerList[index].isExpanded === false ? true : false;
        this.setState({
            data: curMarkerList
        });

        let curExpandedList = curMarkerList.map(function(marker) {
            return marker.isExpanded;
        });
        const key = configKey(this.props);
        this.props.configure_component(this.props.component_index, {[key]: curExpandedList});
    }

    /*
     * click the delete button in the marker detail card
     */
    deleteItemClick = (index, markerName) => (e) => {
        let item = this.state.data[index];
        let record_id = item['id'];
        let deleteAlert = (
            <Alert
                cancelButtonText="Cancel"
                confirmButtonText="Delete"
                isOpen={true}
                intent={Intent.DANGER}
                icon={IconNames.WARNING_SIGN}
                onCancel={this.cancelAlert}
                onConfirm={this.confirmItemDelete.bind(this, record_id, index)}
            >
                <p>Are you sure you wish to delete {markerName} <span className={item.color}>{index+1}</span>?</p>
            </Alert>
        );
        ReactDOM.render(deleteAlert, document.querySelector('#alertdiv'));
    }

    confirmItemDelete = (record_id, index) => {
        this.cancelAlert();
        WrcServer.post('delete-record',
            {table_schema: this.props.table_schema, table_name: this.props.table_name, id: record_id},
            (content) => this.afterDeleteItem(content, index)
        );
    }

    afterDeleteItem = (content, index) => {
        var latest = Array.from(this.state.data);
        latest.splice(index, 1);
        this.setState({data: latest});
        let curExpandedList = latest.map(function(marker) {
            return marker.isExpanded;
        });
        const key = configKey(this.props);
        this.props.configure_component(this.props.component_index, {[key]: curExpandedList});
        this.props.refresh();
    }

    cancelAlert() {
        ReactDOM.unmountComponentAtNode(document.querySelector('#alertdiv'));
    }

    /*
     * click the edit button in the site detail card
     * @param item
     */
    editItemClick = (item) => (e) => {
        let record_id = this.state.data[item]['id'];
        ReactDOM.render(
            <WrcForm
                form_id={this.props.form_id}
                refresh={this.props.refresh}
                fkey={this.props.fkey}
                record_id={record_id}
            />,
            document.querySelector('#form')
        );
    }

    afterEditItem = () => {
        var latest = Array.from(this.state.data);
        this.setState({data: latest});
    }
}

export default WrcImageMap;
